/* eslint react-hooks/rules-of-hooks: 0 */
import * as React from 'react'
import hoistNonReactStatics from 'hoist-non-react-statics'

import LightBox from 'components/LightBox'

export type Images = Array<{ src: string; label: string }>

type LightBoxContextValue = {
  open: (images: Images, index?: number, thumbnail?: string) => void
  close: () => void
}

const defaultContextValue: LightBoxContextValue = {
  open: () => {},
  close: () => {},
}

export const LightBoxContext: React.Context<LightBoxContextValue> =
  React.createContext(defaultContextValue)

type Props = Record<string, any>

export default function LightBoxContextHOCWrapper<
  T extends Record<string, any>,
>(WrappedComponent: React.ComponentType<T>) {
  function lightBoxContextHOC(props: Props & T) {
    const [index, setIndex] = React.useState(0)
    const [images, setImages] = React.useState<Images | null>(null)
    const [thumbnail, setThumbnail] = React.useState<string | null>(null)

    const open = React.useCallback(
      (nextImages: Images, initialIndex?: number, initialThumbnail?: string) => {
        setIndex(initialIndex ?? 0)
        setImages(nextImages)
        setThumbnail(initialThumbnail ?? null)
      },
      [],
    )

    const close = React.useCallback(() => {
      setImages(null)
      setIndex(0)
      setThumbnail(null)
    }, [])

    const contextValue = React.useMemo(() => ({ open, close }), [open, close])

    return (
      <>
        <LightBoxContext.Provider value={contextValue}>
          <WrappedComponent {...props} />
        </LightBoxContext.Provider>
        {Array.isArray(images) && images.length > 0 ? (
          <LightBox items={images} index={index} onClose={close} thumbnail={thumbnail} />
        ) : null}
      </>
    )
  }

  hoistNonReactStatics(lightBoxContextHOC, WrappedComponent)
  return lightBoxContextHOC
}

export function useLightBox() {
  const { open, close } = React.useContext(LightBoxContext)

  return { open, close }
}
